<script lang="ts">
	import StructuredData from "@components/common/StructuredData.svelte";
	import type {
		Course as SchemaCourse,
		CourseInstance as SchemaCourseInstance,
		WithContext as SchemaWithContext,
	} from "schema-dts";
	import type { PublicCourseTerm } from "@core/schema/CourseTerm.js";
	import type { CourseType } from "@core/schema/CourseType.js";
	import { courseTypeContentMap } from "@core/schema/names/courseTypeContentMap.js";
	import { courseTypeNameMap } from "@core/schema/names/courseTypeNameMap.js";
	import { getCourseTypePath } from "@core/schema/paths/getCourseTypePath.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { getCourseTypeGallery } from "../../courseTypeGalleries.js";
	import { SeoFragment } from "../../seo/SeoFragment.js";
	import { idRef, seoId } from "../../seo/seoIds.js";

	interface Props {
		courseType: CourseType;
		terms: PublicCourseTerm[];
	}

	const { courseType, terms }: Props = $props();
	const { WEB_URL_OR_LOCAL } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	const url = $derived(`${WEB_URL_OR_LOCAL}${getCourseTypePath(tenant.locale, courseType)}`);
	const providerRef = $derived(idRef(seoId(WEB_URL_OR_LOCAL, SeoFragment.Organization)));

	function getCourseInstance(term: PublicCourseTerm): SchemaCourseInstance {
		return {
			"@type": "CourseInstance",
			courseMode: "onsite",
			startDate: term.startsAt,
			endDate: term.endsAt,
			location: {
				"@type": "Place",
				name: term.placeName,
				url: term.placeUrl,
			},
		};
	}

	const data = $derived<SchemaWithContext<SchemaCourse>>({
		"@context": "https://schema.org",
		"@type": "Course",
		name: courseTypeNameMap[courseType][tenant.locale],
		description: courseTypeContentMap[courseType].shortDescription[tenant.locale],
		image: getCourseTypeGallery(courseType),
		provider: providerRef,
		url,
		hasCourseInstance: terms.map(getCourseInstance),
	});
</script>

<StructuredData {data} />
